// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * SecondaryActionButton — centrale design-systemcomponent voor de secundaire
 * (ondersteunende) actie naast een PrimaryActionButton, bijvoorbeeld
 * "Terug naar vorige scherm".
 *
 * Eén uniforme, rustige secundaire stijl: witte achtergrond, zachte rand,
 * subtiele tekst, met dezelfde focus-/active-states als de rest van het
 * design system. Standaard een pijl-links-icoon; aanpasbaar via `icon`.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SecondaryActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?:      ReactNode
  fullWidth?: boolean
}

export const SecondaryActionButton = forwardRef<HTMLButtonElement, SecondaryActionButtonProps>(
  ({ icon, fullWidth = true, className, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'flex items-center justify-center gap-2 rounded-2xl border-2 border-sand-300 bg-white',
        'px-5 py-3 text-xs font-medium text-ink-4 transition-all active:scale-[0.98]',
        'hover:border-sage-300 hover:text-ink-3',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sage-400',
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {icon === undefined ? <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> : icon}
      {children && <span>{children}</span>}
    </button>
  ),
)

SecondaryActionButton.displayName = 'SecondaryActionButton'
